import { h } from "preact";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";

import interact, { makeNewRestrictedPosition } from "../../interact";

import { play, pause, backward, xMark } from "../../icons.js";
import { fromJsonApi } from "../../models.js";
import Question from "../../models/question.js";
import { unescapeStr } from "../../utils/xml.js";
import MultipartQuestion from "../../models/multipart-question";
import MultipleChoiceQuestion from "../../models/multiple-choice-question";
import { playAndVerifyAudio } from "./tts-playback.js";

/**
 *
 * @typedef {Object} Options
 * @property {Object | undefined} credentials,
 * @property {() => HTMLElement} fetchReferenceMaterialHtml,
 * @property {string | undefined} outputBucket, 
 * @property {boolean} hasReferenceMaterial,
 * @property {{
 *    fetchQuestionBodyHTML: () => HTMLElement,
 *    fetchChoiceContent: () => (string|HTMLElement)[]
 *    hasChoiceContent: boolean
 *  } | undefined} externalQuestion,
 *  @property {import("../../shared-types").ItemJSON | undefined} assessmentItem
 * }}
 */

/**
 *
 * @typedef {Options & {questionId: string, close: () => void, dragContainer?: string}} Props 
 * }}
 */

/**
 * @type import("../../../tts/index.js").default | null
 */
const initialTtsClientState = null;
const audioPlayer = new Audio();
window["_audioPlayer"] = audioPlayer;

/**
 *
 * @typedef {Object} SpeechMark
 * @property {string} type
  * @property {string} value
  * @property {number} time
*/

/**
 * @type {{url: string, marks: SpeechMark[]}[] | null}
 */
const initialSrcState = null;

/**
 * @type {{root: Element | null, roots: Element[] | null, startNode: Node | null, startNodeOffset: number | null} | null}
 */
const initialHtmlToHighlightState = null;


const finishedPlaybackEvent = new Event('finishedPlayback');

const EmptySrc = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

/**
 * @param {Props} props
 */
export default function (props) {
  let [ttsClient, setTtsClient] = useState(initialTtsClientState);
  let [playing, setPlaying] = useState(false);
  let [srcQueue, setSrcQueue] = useState(initialSrcState);
  let [sourceType, setSourceType] = useState(() => {
    if (documentHasSelection()) {
      return "selection"
    } else {
      return "prompt"
    }
  });

  let [needsNewSrcOnPlay, setNeedsNewSrcOnPlay] = useState(true);

  let [playbackSpeed, setPlaybackSpeed] = useState("medium");
  let [loading, setLoading] = useState(false);
  let [playbackError, setPlaybackError] = useState(/** @type {string | null} */ (null));
  let model = /** @type Question | null */ (useMemo(
    () => props.assessmentItem ? fromJsonApi(props.assessmentItem) : null,
    [props.assessmentItem]
  ));

  let [hasSelection, setHasSelection] = useState(documentHasSelection);
  let [htmlToHighlight, setHtmlToHighlight] = useState(initialHtmlToHighlightState);
  let sourceTypeSelect = useRef(/** @type {HTMLSelectElement | null} */ (null));
  let synthesisRequestId = useRef(0);
  let synthesisController = useRef(/** @type {AbortController | null} */ (null));
  let objectUrls = useRef(new Set());

  let getQuestionBodyHTML = useCallback(() => {
    if (props.externalQuestion) {
      return props.externalQuestion.fetchQuestionBodyHTML();
    } else {
      if (model instanceof Question) {
        let elem = document.querySelector(".question-wrapper .question-body");
        if (elem) {
          return elem;
        }
        throw "Failed to find question body";
      } else {
        throw ("Expected assessment item or external question config")
      }
    }
  }, [props.questionId, props.externalQuestion, model]);

  let getReferenceMaterialHTML = useCallback(() => {
    return props.fetchReferenceMaterialHtml();
  }, [props.questionId, props.fetchReferenceMaterialHtml]);

  let getChoiceHtml = useCallback(
    /**
     * 
     * @param {Question | undefined} focusedModel 
     */
    (focusedModel=undefined) => {
    /**
     * @type {(String | Element)[]}
     */
    let choiceContent = [];
    if (props.externalQuestion) {
      choiceContent = props.externalQuestion.fetchChoiceContent();
    } else if(focusedModel instanceof MultipleChoiceQuestion) {
      /**
       * @type {Element[]}
       */
      let choices = [];
      if(model instanceof MultipartQuestion) {
        choices = Array.from(document.querySelectorAll(`.multipart-part[data-part-id='${focusedModel.id}'] .response`)); 
      } else if(model instanceof MultipleChoiceQuestion) {
        choices = Array.from(document.querySelectorAll(`.question-wrapper[data-question-id='${model.id}'] .response`));
      }
      if(choices.length == 0) {return {ssml: "", roots: []}}
      choices.forEach((choice) => {
        let label = choice.querySelector(".label")?.textContent;
        if(!label) return;
        choiceContent.push("<mark name='highlight_ignore_start'/>Answer <say-as interpret-as='characters'>" + label + "</say-as><break/><mark name='highlight_ignore_end'/>");
        let content = choice.querySelector(".answer");
        if(!content) return;
        choiceContent.push(content);
        if(!content.querySelector("p")) {
          choiceContent.push("<break/>");
        }
      })
    }
    let ssml = "";
    /** @type Element[] */
    let roots = [];
    choiceContent.forEach((stringOrHtml) => {
      if (typeof stringOrHtml == "string") {
        ssml += stringOrHtml
      } else {
        roots.push(stringOrHtml);
        ssml += `<mark name='highlight_jump_${roots.length - 1}'/>${Question.htmlToSSML(stringOrHtml)}`;
      }
    })

    return { roots, ssml };
  }, [props.questionId, props.externalQuestion, model]);

  /**
   * Invalidate pending synthesis and return the player to a retryable state.
   *
   * @param {boolean} resetSourceType
   */
  const clearPlayback = (resetSourceType = false) => {
    synthesisRequestId.current += 1;
    synthesisController.current?.abort();
    synthesisController.current = null;
    audioPlayer.pause();
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.current.clear();
    audioPlayer.removeAttribute("src");
    audioPlayer.load();
    setSrcQueue(null);
    setPlaying(false);
    setLoading(false);
    setNeedsNewSrcOnPlay(true);
    setHtmlToHighlight(null);
    setPlaybackError(null);
    if (resetSourceType) {
      setSourceType("prompt");
    }
  };

  const resetPlayer = () => {
    clearPlayback(true);
  };

  /**
   * @param {unknown} error
   * @param {{url: string}[]} [sources]
   */
  const recoverFromPlaybackError = (error, sources = []) => {
    console.error("Text-to-speech playback failed", error);
    clearPlayback();
    sources.forEach((source) => URL.revokeObjectURL(source.url));
    if (error instanceof Error && error.name == "TimeoutError") {
      setPlaybackError("Text to speech took too long to load. Please try again.");
    } else if (isNetworkError(error)) {
      setPlaybackError("Text to speech lost its network connection. Please try again.");
    } else {
      setPlaybackError("Text to speech could not play this text. Please try again.");
    }
  };

  const togglePlay = async (e) => {
    e.preventDefault();
    if (playing && !audioPlayer.paused && !audioPlayer.ended && !audioPlayer.error) {
      audioPlayer.pause();
      setPlaying(false);
      return;
    }

    setPlaybackError(null);

    // Play is also recovery: replace unfinished work or broken media instead
    // of trusting a UI flag that can outlive the underlying request/player.
    if (needsNewSrcOnPlay || synthesisController.current || playbackError || audioPlayer.error ||
        (srcQueue && !srcQueue.some((source) => source.url == audioPlayer.src))) {
      clearPlayback();
      srcQueue = null;
    }

    if (srcQueue) {
      let requestId = synthesisRequestId.current;
      try {
        if (audioPlayer.ended) {
          audioPlayer.src = srcQueue[0].url;
          audioPlayer.currentTime = 0;
          audioPlayer.dispatchEvent(finishedPlaybackEvent);
        }
        await playAndVerifyAudio(audioPlayer);
        if (requestId != synthesisRequestId.current) return;
        setPlaying(!audioPlayer.ended);
      } catch (error) {
        if (requestId == synthesisRequestId.current) {
          recoverFromPlaybackError(error, srcQueue);
        }
      }
    } else {
      // Consume the current source request before clearing the selection. A
      // subsequent selectionchange must remain pending for the next Play.
      setNeedsNewSrcOnPlay(false);
      /**
       * @type {Element | null}
       */
      let html = null;

      /**
       * @type {Node | null}
       */
      let startNode = null;

      /**
       * @type {number | null}
       */
      let startNodeOffset = null;

      /**
       * @type {Element[] | null}
       */
      let roots = null;

      let ssml = "";
      try {
        if (sourceType == "prompt") {
          html = getQuestionBodyHTML();
          ssml = model ? model.promptSSML() : Question.htmlToSSML(html);
        } else if (sourceType == "reference-material") {
          html = getReferenceMaterialHTML();
          ssml = Question.htmlToSSML(html);
        } else if (sourceType == "answer-choices") {
          let choiceHtml = getChoiceHtml();
          ssml = choiceHtml.ssml;
          roots = choiceHtml.roots;
        } else if (sourceType == "selection") {
          let selection = document.getSelection();
          if (!selection || selection.rangeCount == 0) { return };

          let range = selection.getRangeAt(0);
          // A text node may be split by highlighting; keep a stable root that
          // also contains the new sibling text nodes.
          html = range.commonAncestorContainer instanceof Element
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentElement;
          startNode = range.startContainer;
          startNodeOffset = range.startOffset;

          let contents = range.cloneContents();
          var div = document.createElement('div');
          div.appendChild(contents);

          //if the selection is for the question body we should apply additional processing
          if (range.commonAncestorContainer instanceof HTMLElement &&
            range.commonAncestorContainer.closest(".question-body") &&
            model instanceof Question) {

            model.processPromptMarkupForTts(div)
          }

          ssml = Question.htmlToSSML(div);
          selection.removeAllRanges();
        } else if (sourceType.startsWith("part_")) {
          if(!(model instanceof MultipartQuestion)) {return}
          let [_p, id, type] = sourceType.split("_");
          let part = model.parts.find((part) => {
            return String(part.id) == id
          });
          if(!part) return;
          if(type == "prompt") {
            html = document.querySelector(`.multipart-part[data-part-id='${part.id}'] .question-body`);
            ssml = part.promptSSML();
          } else if(type == "answer-choices") {
            let choiceHtml = getChoiceHtml(part);
            ssml = choiceHtml.ssml;
            roots = choiceHtml.roots;
          }
        }
      } catch (error) {
        recoverFromPlaybackError(error);
        return;
      }

      if ((!html && !roots) || !ssml.trim()) {
        setNeedsNewSrcOnPlay(true);
        return
      }

      setHtmlToHighlight({ root: html, roots, startNode, startNodeOffset });

      if (!ttsClient) {
        recoverFromPlaybackError(new Error("The text-to-speech client is not ready"));
        return;
      }

      let requestId = synthesisRequestId.current + 1;
      synthesisRequestId.current = requestId;
      const controller = new AbortController();
      synthesisController.current = controller;
      setLoading(true);

      audioPlayer.src = EmptySrc;
      audioPlayer.play().catch((e) => { console.error(e) });

      /** @type {{url: string, marks: SpeechMark[]}[]} */
      let generatedSources = [];
      try {
        let urls = await ttsClient.synthesizeSpeech(ssml, playbackSpeed, { signal: controller.signal });
        generatedSources = urls;
        if (requestId != synthesisRequestId.current) {
          urls.forEach((source) => URL.revokeObjectURL(source.url));
          return;
        }

        if (urls.length == 0) {
          throw new Error("Text-to-speech returned no audio");
        }

        urls = urls.map((url) => {
          return { ...url, marks: filterMarks(url.marks) }
        });
        urls.forEach((source) => objectUrls.current.add(source.url));
        setSrcQueue(urls);
        audioPlayer.src = urls[0].url;
        await playAndVerifyAudio(audioPlayer);
        if (requestId != synthesisRequestId.current) return;
        synthesisController.current = null;
        setLoading(false);
        setPlaying(!audioPlayer.ended);
      } catch (error) {
        if (requestId == synthesisRequestId.current) {
          recoverFromPlaybackError(error, generatedSources);
        }
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    import("../../../tts/index.js").then(({ default: client }) => {
      if (cancelled) return;
      let clientInstance = new client({
        credentials: props.credentials,
        outputBucket: props.outputBucket,
      });
      setTtsClient(clientInstance);
    });

    return function cleanup() {
      cancelled = true;
    };
  }, [props.credentials, props.outputBucket]);

  useEffect(() => {
    let position = {x: 0, y: 0};

    interact(".tts-floating-window").draggable({
      inertia: true,
      allowFrom: ".drag-handle",
      autoScroll: {
        enabled: true
      },
      listeners: {
        move: (evt) => {
          position = makeNewRestrictedPosition(evt, position);
          if (evt.target) {
            evt.target.style.transform = `translate(${position.x}px, ${position.y}px)`
          }
        },
      }
    });

    return function cleanup() {
      resetPlayer();
    };
  }, []);

  // Cancel old playback before the new question can receive user input.
  useLayoutEffect(() => {
    resetPlayer();
  }, [props.questionId]);

  useEffect(() => {
    let callback = () => {
      let selection = document.getSelection();
      let hasSelection = selection !== null && !selection.isCollapsed;
      setHasSelection(hasSelection)
      if (hasSelection) {
        setSourceType("selection")
        setNeedsNewSrcOnPlay(true);
      } else if (!hasSelection && sourceTypeSelect.current?.value == "selection") {
        setSourceType("prompt")
        setNeedsNewSrcOnPlay(true);
      }
    };

    document.addEventListener("selectionchange", callback);
    return function cleanup() {
      document.removeEventListener("selectionchange", callback);
    }
  }, []);

  useEffect(() => {
    function endedCallback() {
      const queue = srcQueue;
      if (queue) {
        let currentSrcIndex = queue.findIndex((src) => src.url == audioPlayer.src);
        // Ignore warmup/old media events while a fresh synthesis is starting.
        if (currentSrcIndex < 0) return;
        if (currentSrcIndex == queue.length - 1) {
          audioPlayer.dispatchEvent(finishedPlaybackEvent);
          audioPlayer.pause();
          setPlaying(false);
        } else {
          let requestId = synthesisRequestId.current;
          audioPlayer.src = queue[currentSrcIndex + 1].url;
          audioPlayer.load();
          playAndVerifyAudio(audioPlayer).catch((error) => {
            if (requestId == synthesisRequestId.current) {
              recoverFromPlaybackError(error, queue);
            }
          });
        }
      }
    }

    function errorCallback() {
      const queue = srcQueue;
      if (queue && queue.some((source) => source.url == audioPlayer.src)) {
        recoverFromPlaybackError(
          new Error(audioPlayer.error?.message || "Text-to-speech audio could not be decoded"),
          queue
        );
      }
    }

    audioPlayer.addEventListener("ended", endedCallback);
    audioPlayer.addEventListener("error", errorCallback);

    return function cleanup() {
      audioPlayer.removeEventListener("ended", endedCallback);
      audioPlayer.removeEventListener("error", errorCallback);
    }
  }, [srcQueue])

  useEffect(() => {
    // Initialize once the audio is ready, so selection offsets are applied
    // only once rather than splitting the start node again after synthesis.
    if (!htmlToHighlight || !srcQueue) return;

    let marker = document.createElement("span");
    marker.classList.add("tts-highlight");

    /**
     * 
     * @param {Node | undefined} currentNode 
     * @returns 
     */
    function buildWalker(currentNode = undefined) {
      if (!htmlToHighlight) return;
      let initialRoot = htmlToHighlight.root || (htmlToHighlight.roots ? htmlToHighlight.roots[0] : null);
      if (!initialRoot) return;
      let walker = document.createTreeWalker(initialRoot, NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT, {
        acceptNode(node) {
          if (node instanceof HTMLElement) {
            if (['video', 'audio', 'iframe', 'title', 'style', 'script'].includes(node.tagName.toLowerCase())) {
              return NodeFilter.FILTER_REJECT
            }

            if (node.classList.contains("drawing-editor")) {
              return NodeFilter.FILTER_REJECT
            }

            return NodeFilter.FILTER_ACCEPT
          } else {
            return NodeFilter.FILTER_ACCEPT
          };
        }
      });

      if (htmlToHighlight.startNode && initialRoot.contains(htmlToHighlight.startNode)) {
        let startNode = htmlToHighlight.startNode;
        if (htmlToHighlight.startNodeOffset && htmlToHighlight.startNodeOffset > 0) {
          if (startNode instanceof Text) {
            if (htmlToHighlight.startNodeOffset <= startNode.length) {
              walker.currentNode = startNode.splitText(htmlToHighlight.startNodeOffset);
            } else {
              walker.currentNode = startNode;
            }
          } else {
            let offsetNode = startNode.childNodes.item(htmlToHighlight.startNodeOffset);
            walker.currentNode = offsetNode || startNode;
          }
        } else {
          walker.currentNode = startNode;
        }
      }

      if (currentNode && initialRoot.contains(currentNode)) {
        walker.currentNode = currentNode;
      }

      return walker;
    }

    let maybeWalker = buildWalker();
    if (!maybeWalker) return;

    let walker = maybeWalker;

    let lastMark = null;
    let cancel = false;

    function clearMarker() {
      let maybeParent = marker.parentElement;
      if (maybeParent) {
        let parent = maybeParent;
        marker.childNodes.forEach((node) => {
          parent.insertBefore(node, marker);
        })

        parent.removeChild(marker);
      }
    }

    /**
     * 
     * @param {SpeechMark} instructionMark 
     */
    function processInstructionMark(instructionMark) {
      if (instructionMark.value.startsWith("highlight_jump") && htmlToHighlight && htmlToHighlight.roots && htmlToHighlight.roots.length > 0) {
        let jumpIndex = parseInt(instructionMark.value.split("_")[2]);
        let jumpRoot = htmlToHighlight.roots[jumpIndex];
        if (jumpRoot) {
          walker = document.createTreeWalker(jumpRoot, NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT, null);
        }
      }
    }

    let lastTime = 0;

    function renderHighlight() {
      let currentSrc = srcQueue?.find((src) => { return src.url == audioPlayer.src });
      // A queued frame can run after finishedPlayback clears the marker.
      // Do not redraw the last mark once the audio has ended.
      if (cancel || !currentSrc || audioPlayer.ended) {
        return;
      }

      let currentTime = (audioPlayer.currentTime * 1000);
      if (currentTime <= lastTime && !audioPlayer.paused) {
        requestAnimationFrame(renderHighlight);
        return;
      }

      lastTime = currentTime;

      let closestMark = null;
      /** @type SpeechMark | null*/
      let instructionMark = null;
      for (let mark of currentSrc.marks ?? []) {
        if (mark.time > currentTime) {
          if (mark.type == "ssml") {
            instructionMark = mark;
            continue;
          } else {
            break;
          }
        }

        if (mark.type != "ssml") {
          closestMark = mark;
        }
      }

      if (instructionMark) {
        if (closestMark && closestMark.time > instructionMark.time) {
          processInstructionMark(instructionMark);
          instructionMark = null
        } else if (!closestMark) {
          processInstructionMark(instructionMark);
          instructionMark = null
        }
      }

      if (closestMark) {
        if (closestMark != lastMark) {
          clearMarker()

          /** @type Node | null */
          let currentNode = walker.currentNode;
          let originalNode = currentNode;
          while (currentNode) {
            if (!currentNode) {
              break;
            }

            if (currentNode.nodeType == Node.TEXT_NODE) {
              let textNode = /** @type {Text} */ (currentNode);

              let textToFind = unescapeStr(closestMark.value);
              let idx = textNode.textContent?.indexOf(textToFind);
              if (idx !== undefined && idx >= 0) {
                let textNodeToHighlight = textNode.splitText(idx);
                let nextTextNode = textNodeToHighlight.splitText(textToFind.length);
                marker.appendChild(textNodeToHighlight);
                textNode.parentNode?.insertBefore(marker, nextTextNode);
                walker.currentNode = nextTextNode;
                lastMark = closestMark;
                break;
              }
            }

            currentNode = walker.nextNode();
            if (!currentNode) {
              lastMark = closestMark;
              let maybeWalker = buildWalker(originalNode);
              if (!maybeWalker) return;
              walker = maybeWalker;

              break
            };
          }
        }
      }

      if (instructionMark) {
        processInstructionMark(instructionMark);
      }

      if (!audioPlayer.paused) {
        requestAnimationFrame(renderHighlight);
      }
    }

    let playCallback = () => {
      lastTime = 0;
      requestAnimationFrame(renderHighlight);
    };

    //When playback has finished make a new walker
    let finishedPlaybackCallback = () => {
      lastTime = 0;
      lastMark = null;
      clearMarker();
      let maybeWalker = buildWalker();
      if (!maybeWalker) return;
      walker = maybeWalker;
    };

    if (!audioPlayer.paused) {
      requestAnimationFrame(renderHighlight);
    }
    audioPlayer.addEventListener("play", playCallback);
    audioPlayer.addEventListener("finishedPlayback", finishedPlaybackCallback);

    return () => {
      clearMarker();
      audioPlayer.removeEventListener("play", playCallback);
      audioPlayer.removeEventListener("finishedPlayback", finishedPlaybackCallback);
      cancel = true
    }
  }, [htmlToHighlight, srcQueue]);

  return (
    <div className={playbackError ? "tts-floating-window has-error" : "tts-floating-window"}>
      <div class="drag-handle tts-floating-window-header">
        <div class="tts-floating-window-title">Text to Speech</div>
        <span class="tts-floating-window-exit" dangerouslySetInnerHTML={{ __html: xMark }} onClick={props.close}></span>
      </div>
      <div class="controls">
        <button
          type="button"
          aria-label={loading ? "Restart text to speech" : playing ? "Pause text to speech" : "Play text to speech"}
          className={loading ? "play item-player-loading" : "play"}
          dangerouslySetInnerHTML={{
            __html: loading ? "" : playing ? pause : play,
          }}
          onPointerDown={(e) => { e.preventDefault() }}
          onClick={togglePlay}
        ></button>

        <span
          className="rewind"
          dangerouslySetInnerHTML={{ __html: backward }}
          onClick={(e) => {
            e.preventDefault()
            audioPlayer.dispatchEvent(finishedPlaybackEvent);
            audioPlayer.currentTime = 0;
          }}
        ></span>
        <div className="playback-speed">
          Speed:
          <select
            value={playbackSpeed}
            onChange={(event) => {
              clearPlayback();
              setPlaybackSpeed(event.currentTarget.value);
            }}
          >
            <option value="slow">Slow</option>
            <option value="medium">Normal</option>
            <option value="fast">Fast</option>
          </select>
        </div>

        <div className="type-select">
          Play:
          <select
            value={sourceType}
            ref={sourceTypeSelect}
            onChange={(event) => {
              resetPlayer();
              let value = event.currentTarget.value;
              setSourceType(value);
              setNeedsNewSrcOnPlay(true);
              let selection = document.getSelection();
              if (selection) { selection.removeAllRanges() };
            }}
          >


            {props.questionId ? <option value="prompt">Question</option> : null}
            {(props.externalQuestion?.hasChoiceContent || model && model instanceof Question && model.hasChoiceContentForTts()) ? (
              <option value="answer-choices">Answer Choices</option>
            ) : null}
            {props.hasReferenceMaterial ? (
              <option value="reference-material">Passage</option>
            ) : null}
            {(model && model instanceof MultipartQuestion) ? (
              model.parts.map((part, index) => {
                let partId =  part.id;
                let rtn = [];
                rtn.push(<option value={`part_${partId}_prompt`}>Part {String.fromCharCode(index + 65)}</option>)
                if(part.hasChoiceContentForTts()) {
                  rtn.push(<option value={`part_${partId}_answer-choices`}>Part {String.fromCharCode(index + 65)} Answer Choices</option>)
                }
                return rtn;
              })
            ) : null}
            <option value="selection" disabled={!hasSelection}>Selected Text</option> :
          </select>
        </div>
        {playbackError ? <div className="tts-error" role="alert">{playbackError}</div> : null}
      </div>
    </div>
  );
}

function documentHasSelection() {
  let selection = document.getSelection();
  return !!(selection && !selection.isCollapsed && selection.rangeCount > 0)
}

/**
 * @param {unknown} error
 */
function isNetworkError(error) {
  if (!error || typeof error != "object") return false;
  let networkError = /** @type {{name?: string, message?: string}} */ (error);
  return networkError.name == "NetworkError" ||
    (networkError.name == "TypeError" && /fetch|network|load failed|socket/i.test(networkError.message || ""));
}

/**
 *
 * @param {HTMLElement | undefined} container 
 */
function dragMoveListener(container) {
  /**
   * 
   * @param {import("@interactjs/types").InteractEvent} event 
   */
  return function (event) {
    var target = event.target;

    var offset = 0;
    if (container) {
      var originalScrollTop = target.getAttribute("data-scroll-top")
      if (!originalScrollTop) {
        originalScrollTop = String(container.scrollTop);
        target.setAttribute("data-scroll-top", originalScrollTop)
      }

      var currentScrollTop = container.scrollTop;
      offset = parseFloat(originalScrollTop) - currentScrollTop;
    }

    // keep the dragged position in the data-x/data-y attributes
    var x = (parseFloat(target.getAttribute("data-x") + "") || 0) + event.dx;
    var y = (parseFloat(target.getAttribute("data-y") + "") || 0) + event.dy;

    // translate the element
    target.style.transform = "translate(" + x + "px, " + (y - offset) + "px)";

    // update the posiion attributes
    target.setAttribute("data-x", String(x));
    target.setAttribute("data-y", String(y));
  };
};

/**
 * 
 * @param {SpeechMark[]} marks 
 */
function filterMarks(marks) {
  let currentlyIgnoring = false;

  return marks.filter((mark) => {
    if (mark.type == "ssml") {
      if (mark.value == "highlight_ignore_start") {
        currentlyIgnoring = true;
        return false
      } else if (mark.value == "highlight_ignore_end") {
        currentlyIgnoring = false;
        return false
      }
      return true;
    }

    return !currentlyIgnoring
  })
}
